import * as React from 'react';

import type {
    SessionUser as SessionUserFragment,
    EmailAddress as EmailAddressFragment,
    RelatedEmailAddresses as RelatedEmailAddressesFragment,
    EntityRelatedEmailAddresses as EntityRelatedEmailAddressesFragment,
    EmailableLead,
} from 'nutshell-graphql-types';

import {createFromScratchTemplate} from '../create-from-scratch-template';
import {CREATE_FROM_SCRATCH_ID} from '../types';

import {EmailSenderGraphqlNew} from './email-sender-graphql-new';

type OwnProps = {
    entity: EntityRelatedEmailAddressesFragment;
    relatedEmailAddresses: RelatedEmailAddressesFragment;
    relatedLeads: EmailableLead[];
    user: SessionUserFragment;
    closeModal: () => void;
    seededWithEmailAddress: string | null | undefined;
    seedWithAllPrimaryRelatedEmailAddresses: boolean | null | undefined;
    shouldRestoreFromStorage: boolean;
    onEmailEdited: () => void;
};

export const EmailSenderNew = (props: OwnProps) => {
    // We want to spread all the props to EmailSenderGraphqlNew except these two
    // which are used in getInitialToAddresses but not used further down the
    // component hierarchy
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {seedWithAllPrimaryRelatedEmailAddresses, seededWithEmailAddress, ...restProps} = props;

    const shouldSkip = props.shouldRestoreFromStorage || !getEntityIdToMaterialize(props);
    const createFromScratch = {
        [CREATE_FROM_SCRATCH_ID]: createFromScratchTemplate(props.user),
    } as const;

    const initialValues = {
        fromAddress: props.user.sendableEmailAddresses[0],
        toAddresses: getInitialToAddresses(props),
        ccAddresses: [],
        sequences: createFromScratch,
        templateGroups: [],
        uncategorizedTemplates: [],
        recentlySentTemplates: [],
        relatedLeadIds: props.entity.emailableLeads.map((lead) => lead.id),
        selectedSequenceId: CREATE_FROM_SCRATCH_ID,
        selectedTemplateId: CREATE_FROM_SCRATCH_ID,
        isRecentSelected: false,
    } as const;

    return (
        <EmailSenderGraphqlNew
            {...restProps}
            // @ts-expect-error
            initialValues={initialValues && !shouldSkip ? initialValues : undefined}
            createFromScratchTemplate={createFromScratch}
        />
    );
};

/**
 * Helper function to determine which addresses to seed the email drip composer
 * with in the "To" field.
 *
 * Normally, we'll just return the primary email on the entity and seed the
 * modal with that, but if an address was clicked on the sidebar, we'll want to
 * seed it with _that_ address.
 *
 * @param  {Object} props     - Full props for this component
 * @return {Array}            - Array of email address objects
 */
function getInitialToAddresses(props: OwnProps): EmailAddressFragment[] {
    if (props.seedWithAllPrimaryRelatedEmailAddresses) {
        // @ts-expect-error
        const primaryRelatedEmailAddresses = [];

        const seen = new Set();
        const allEmails = props.relatedEmailAddresses.all;

        allEmails.forEach((email) => {
            if (email && email.value && email.entity && email.entity.primaryEmailAddress) {
                const primary = email.entity.primaryEmailAddress;

                if (!seen.has(primary)) {
                    seen.add(primary);
                    primaryRelatedEmailAddresses.push(email);
                }
            }
        });

        // @ts-expect-error
        return primaryRelatedEmailAddresses;
    } else if (props.seededWithEmailAddress) {
        const clickedAddress = props.relatedEmailAddresses.all.find(
            (relatedEmailAddress: EmailAddressFragment) => {
                return relatedEmailAddress.value.address === props.seededWithEmailAddress;
            }
        );

        // If we couldn't find the clicked address in our related emails
        // instead of returning the wrong data, seed with nothing
        return clickedAddress ? [clickedAddress] : [];
    } else if (props.relatedEmailAddresses.primary) {
        return [props.relatedEmailAddresses.primary];
    } else {
        return [];
    }
}

function getEntityIdToMaterialize(props: OwnProps): string {
    const initialToAddresses = getInitialToAddresses(props);

    // @ts-expect-error
    return initialToAddresses.length ? initialToAddresses[0].entity.id : props.entity.id;
}
